// Repo Governance — connect GitHub, choose which repositories Aviary may read,
// and what to check in each (ADR-016).
//
// Repository selection happens twice on purpose: once in GitHub's own install
// UI (which is what actually grants access), and again here per repo (which is
// what we act on). The second gate means an org-wide install doesn't silently
// start commenting on every repository the moment it is connected.

import { ExternalLink, Github, RefreshCw } from "lucide-react";
import { useSearchParams } from "@/lib/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { SeverityBadge, When } from "@/components/governance/bits";
import { ChecklistDialog, useRepoChecks } from "@/components/governance/checklist-dialog";
import { GovernanceHeader, useIsGovernanceAdmin } from "@/components/governance/nav";
import { Link } from "@/components/ui/link";
import { toast } from "@/components/ui/toaster";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import {
  completeGithubInstall,
  disconnectGithub,
  listGovernedFiles,
  startRepoScan,
  syncGovernedRepo,
  updateGovernedRepo,
} from "@/lib/api/endpoints";
import { useGithubInstallation, useGovernedRepos } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { GovernanceSeverity, GovernedFileView, GovernedRepoView } from "@/types/api";

export default function GithubSettingsPage() {
  const installation = useGithubInstallation();

  return (
    <div className="space-y-6">
      <GovernanceHeader description="Connect a GitHub organisation, choose which repositories Aviary governs, and pick what to check in each: AI agent files, secrets, sensitive files, dependencies, insecure code, CI/CD, infrastructure, personal data, licenses and repository settings." />

      <InstallCallbackHandler />

      {installation.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : installation.isError ? (
        <ErrorState error={installation.error} onRetry={() => void installation.refetch()} />
      ) : (
        <>
          <ConnectionCard />
          {installation.data?.connected ? <RepoTable /> : null}
        </>
      )}
    </div>
  );
}

/**
 * GitHub redirects back here after an install with `installation_id`, the
 * `state` we signed, and an OAuth `code` for the user who installed. Completing
 * the binding from the URL means the tenant never has to copy an id by hand —
 * the state, not the session, decides which tenant the installation attaches
 * to, and the code proves the installer can actually see it.
 */
function InstallCallbackHandler() {
  const params = useSearchParams();
  const qc = useQueryClient();
  const installationId = params.get("installation_id");
  const state = params.get("state");
  const code = params.get("code");
  const done = React.useRef(false);

  const complete = useMutation({
    mutationFn: () => completeGithubInstall(Number(installationId), state ?? "", code ?? ""),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.githubInstallation });
      void qc.invalidateQueries({ queryKey: qk.governedRepos });
      // Drop the one-time params so a refresh doesn't retry a spent state.
      window.history.replaceState({}, "", "/github");
    },
  });

  React.useEffect(() => {
    if (done.current || !installationId || !state) return;
    done.current = true;
    complete.mutate();
  }, [installationId, state, complete]);

  if (!installationId) return null;
  if (complete.isError) return <ErrorState error={complete.error} />;
  if (complete.isPending) return <Skeleton className="h-10 w-full" />;

  // Installed straight from github.com rather than from here, so there is no
  // signed state and nothing proves which Aviary tenant the installation is
  // for. We deliberately do NOT fall back to the current session: an attacker
  // who learns an installation id could then attach someone else's
  // organisation to their own tenant and read its private repositories
  // (ADR-016). Say so plainly instead of appearing to do nothing.
  if (!state) {
    return (
      <Card className="border-[hsl(var(--warning))]/40">
        <CardHeader>
          <CardTitle>Finish connecting this installation</CardTitle>
          <CardDescription>
            The Aviary app is installed on GitHub (installation <code>{installationId}</code>), but
            it was started from github.com rather than from here, so it isn&apos;t linked to your
            tenant yet. For security, the link has to begin in Aviary &mdash; otherwise anyone who
            learned this installation id could attach your organisation to their own account. Use
            the button below; GitHub will send you straight back and the connection completes
            automatically. Nothing is reinstalled.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return null;
}

function ConnectionCard() {
  const qc = useQueryClient();
  const isAdmin = useIsGovernanceAdmin();
  const { data } = useGithubInstallation();

  const disconnect = useMutation({
    mutationFn: disconnectGithub,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.githubInstallation });
      void qc.invalidateQueries({ queryKey: qk.governedRepos });
    },
  });

  if (!data) return null;

  if (!data.configured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Not available on this deployment</CardTitle>
          <CardDescription>
            Repo Governance needs GitHub App credentials, which an operator configures on the
            server. Ask your Aviary operator to enable it.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!data.connected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" /> Connect your organisation
          </CardTitle>
          <CardDescription>
            You choose which repositories Aviary can see, in GitHub&apos;s own installation screen.
            Aviary only ever reads code; it can post a check and a comment on a pull request, and
            cannot push, merge, or change branch protection.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isAdmin ? (
            <p className="text-sm text-muted-foreground">
              Ask an org admin to connect your GitHub organisation.
            </p>
          ) : data.install_url ? (
            <Button asChild>
              <a href={data.install_url}>
                Install the Aviary GitHub App <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              The GitHub App slug is not configured on this deployment.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Github className="h-5 w-5" /> {data.account_login}
            </CardTitle>
            <CardDescription className="mt-1 flex items-center gap-2">
              <Badge variant="success">connected</Badge>
              <span>{data.account_type}</span>
              {data.connected_at ? <When iso={data.connected_at} /> : null}
            </CardDescription>
          </div>
          {isAdmin ? (
            <Button
              variant="outline"
              size="sm"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
              title="Stops governance. Findings and history are kept."
            >
              Disconnect
            </Button>
          ) : null}
        </div>
      </CardHeader>
      {disconnect.isError ? (
        <CardContent>
          <ErrorState error={disconnect.error} />
        </CardContent>
      ) : null}
    </Card>
  );
}

function RepoTable() {
  const qc = useQueryClient();
  const isAdmin = useIsGovernanceAdmin();
  const repos = useGovernedRepos(true);
  const catalogue = useRepoChecks();
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const [checklistFor, setChecklistFor] = React.useState<GovernedRepoView | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.governedRepos });
    void qc.invalidateQueries({ queryKey: ["my", "governance", "security"] });
  };

  const toggle = useMutation({
    mutationFn: ({ repoId, enabled }: { repoId: number; enabled: boolean }) =>
      updateGovernedRepo(repoId, { enabled }),
    onSuccess: invalidate,
  });

  const sync = useMutation({
    mutationFn: (repoId: number) => syncGovernedRepo(repoId),
    onSuccess: invalidate,
  });

  const scan = useMutation({
    mutationFn: ({ repoId, kind }: { repoId: number; kind: "full" | "history" }) =>
      startRepoScan(repoId, kind),
    onSuccess: (s) => {
      invalidate();
      toast.success(
        s.kind === "history" ? "Scanning recent git history…" : "Security scan started.",
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // While a scan runs, keep the table fresh so the badge moves to "done".
  const busy = (repos.data ?? []).some(
    (r) => r.last_scan && (r.last_scan.status === "queued" || r.last_scan.status === "running"),
  );
  React.useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: qk.governedRepos }), 5000);
    return () => clearInterval(t);
  }, [busy, qc]);

  if (repos.isLoading) return <Skeleton className="h-64 w-full" />;
  if (repos.isError) return <ErrorState error={repos.error} onRetry={() => void repos.refetch()} />;
  if (!repos.data?.length) {
    return (
      <EmptyState
        title="The installation cannot see any repositories"
        hint="Adjust the Aviary app's repository access in your GitHub organisation settings, then refresh."
      />
    );
  }

  const totalChecks = catalogue.data?.length ?? 11;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Repositories</CardTitle>
            <CardDescription>
              {isAdmin
                ? "Turn governance on per repository and choose its checks. Enabling one reads its agent-config files and runs a full security scan; every pull request and every push to the default branch is scanned after that."
                : "Repositories Aviary governs and what it checks in each. Only org admins can change them."}
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => void repos.refetch()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <THead>
            <TR>
              <TH>Governed</TH>
              <TH>Repository</TH>
              <TH>Checks</TH>
              <TH>Security</TH>
              <TH>Agent files</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {repos.data.map((repo) => (
              <React.Fragment key={repo.github_repo_id}>
                <TR className="align-top">
                  <TD>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[hsl(var(--primary))]"
                      checked={repo.enabled}
                      disabled={!isAdmin || toggle.isPending}
                      onChange={(e) =>
                        toggle.mutate({
                          repoId: repo.github_repo_id,
                          enabled: e.target.checked,
                        })
                      }
                      aria-label={`Govern ${repo.full_name}`}
                    />
                  </TD>
                  <TD>
                    <div className="text-sm font-medium">{repo.full_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {repo.private ? "private" : "public"} · {repo.default_branch}
                    </div>
                  </TD>
                  <TD>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setChecklistFor(repo)}
                      title={isAdmin ? "Choose what to scan" : "See what is scanned"}
                    >
                      {repo.checks.length} of {totalChecks}
                    </Button>
                  </TD>
                  <TD>
                    {repo.enabled ? (
                      <SecurityCell repo={repo} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TD>
                  <TD>
                    {repo.checks.includes("agent_config") ? (
                      <div className="space-y-1">
                        <BaselineBadge repo={repo} />
                        {repo.enabled ? (
                          <div className="text-xs text-muted-foreground">
                            {repo.file_count} files
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">off</span>
                    )}
                  </TD>
                  <TD className="text-right">
                    {repo.enabled ? (
                      <div className="flex flex-wrap justify-end gap-2">
                        {repo.checks.includes("agent_config") ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setExpanded(
                                expanded === repo.github_repo_id ? null : repo.github_repo_id,
                              )
                            }
                          >
                            {expanded === repo.github_repo_id ? "Hide files" : "Agent files"}
                          </Button>
                        ) : null}
                        {isAdmin ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={
                                scan.isPending ||
                                repo.last_scan?.status === "queued" ||
                                repo.last_scan?.status === "running"
                              }
                              onClick={() =>
                                scan.mutate({ repoId: repo.github_repo_id, kind: "full" })
                              }
                            >
                              Scan now
                            </Button>
                            {repo.checks.includes("git_history") ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={scan.isPending}
                                onClick={() =>
                                  scan.mutate({ repoId: repo.github_repo_id, kind: "history" })
                                }
                                title="Secrets in recent commits that are no longer in the files"
                              >
                                Scan history
                              </Button>
                            ) : null}
                            {repo.checks.includes("agent_config") ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={sync.isPending || repo.baseline_status === "syncing"}
                                onClick={() => sync.mutate(repo.github_repo_id)}
                                title="Re-read the agent-config files"
                              >
                                Re-read agent files
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </TD>
                </TR>
                {expanded === repo.github_repo_id ? (
                  <TR>
                    <TD colSpan={6} className="bg-muted/20">
                      <BaselineFiles repoId={repo.github_repo_id} />
                    </TD>
                  </TR>
                ) : null}
              </React.Fragment>
            ))}
          </TBody>
        </Table>
      </CardContent>
      {toggle.isError || sync.isError ? (
        <CardContent>
          <ErrorState error={toggle.error ?? sync.error} />
        </CardContent>
      ) : null}
      {checklistFor ? (
        <ChecklistDialog
          repo={checklistFor}
          open={checklistFor !== null}
          onOpenChange={(open) => !open && setChecklistFor(null)}
          canEdit={isAdmin}
        />
      ) : null}
    </Card>
  );
}

/** Open findings by severity, and how the last scan went. */
function SecurityCell({ repo }: { repo: GovernedRepoView }) {
  const scan = repo.last_scan;
  const order: GovernanceSeverity[] = ["critical", "high", "medium", "low"];
  const counts = order.filter((s) => (repo.security_open[s] ?? 0) > 0);
  return (
    <div className="space-y-1">
      {counts.length ? (
        <Link
          href={`/governance/security?repo=${repo.github_repo_id}`}
          className="flex flex-wrap gap-1"
          title="Open findings on the default branch"
        >
          {counts.map((s) => (
            <span key={s} className="inline-flex items-center gap-1">
              <SeverityBadge severity={s} />
              <span className="text-xs tabular-nums">{repo.security_open[s]}</span>
            </span>
          ))}
        </Link>
      ) : scan?.status === "complete" ? (
        <Badge variant="success">no open issues</Badge>
      ) : null}
      <div className="text-xs text-muted-foreground">
        {!scan ? (
          "not scanned yet"
        ) : scan.status === "queued" || scan.status === "running" ? (
          "scanning…"
        ) : scan.status === "failed" ? (
          <span title={scan.last_error ?? undefined}>last scan failed</span>
        ) : (
          <span title={scan.partial_error ?? undefined}>
            {scan.partial_error ? "partial · " : ""}scanned <When iso={scan.completed_at} />
          </span>
        )}
      </div>
    </div>
  );
}

function BaselineBadge({ repo }: { repo: GovernedRepoView }) {
  if (!repo.enabled) return <span className="text-xs text-muted-foreground">not governed</span>;
  if (repo.baseline_status === "syncing") return <Badge variant="secondary">reading…</Badge>;
  if (repo.baseline_status === "failed") {
    return (
      <span title={repo.last_error ?? undefined}>
        <Badge variant="destructive">failed</Badge>
      </span>
    );
  }
  if (repo.baseline_status === "ready") {
    return (
      <span className="flex items-center gap-2">
        <Badge variant="success">ready</Badge>
        <When iso={repo.baseline_synced_at} />
      </span>
    );
  }
  return <Badge variant="secondary">not read yet</Badge>;
}

/** The agent-config files Aviary currently holds for a repository. */
function BaselineFiles({ repoId }: { repoId: number }) {
  const [files, setFiles] = React.useState<GovernedFileView[] | null>(null);
  const [error, setError] = React.useState<unknown>(null);

  React.useEffect(() => {
    let cancelled = false;
    listGovernedFiles(repoId)
      .then((rows) => !cancelled && setFiles(rows))
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, [repoId]);

  if (error) return <ErrorState error={error} />;
  if (!files) return <Skeleton className="h-16 w-full" />;
  if (!files.length) {
    return (
      <p className="py-2 text-sm text-muted-foreground">
        No agent-config files found on the default branch yet.
      </p>
    );
  }

  return (
    <ul className="grid gap-1 py-2 sm:grid-cols-2">
      {files.map((file) => (
        <li key={file.id} className="flex items-center justify-between gap-3 text-xs">
          <code className="truncate">{file.path}</code>
          <span className="shrink-0 text-muted-foreground">{file.kind.replace(/_/g, " ")}</span>
        </li>
      ))}
    </ul>
  );
}
